'use client';

import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { FormProvider, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useAuth } from 'react-oidc-context';
import styled from 'styled-components';

import { Error } from '@zoomit/icons';
import { Box, Col, Flex, Spacing } from '@zoomit/primitives';
import { isMobile } from '@zoomit/web-kit';
import { Button, cssVar, TextArea, theme, Typography } from '@zoomit/zoomit-ds';

import { reportProduct } from '../../apis/product/single-product';
import type { ProductReportForm } from '../../types/single-product';
import { reportProductSchema } from '../../validations/single';

type Props = {
  onClose: () => void;
  productId: string;
};

const ProblemReportContent = (props: Props) => {
  const { onClose, productId } = props;

  const session = useAuth();

  const formMethods = useForm<ProductReportForm>({
    defaultValues: {
      description: '',
    },
    mode: 'onSubmit',
    resolver: yupResolver(reportProductSchema),
  });

  const {
    handleSubmit,
    register,
    formState: { errors },
  } = formMethods;

  const { isLoading, mutate: reportProblemMutate } = useMutation(({ description }: ProductReportForm) =>
    reportProduct({ productId, description, token: session.user?.access_token as string }),
  );

  const onSubmit = async (data: ProductReportForm) => {
    reportProblemMutate(data, {
      onSuccess: () => {
        toast.success('گزارش شما با موفقیت ثبت شد');
        onClose();
      },
    });
  };

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box
          spacing={{ pt: 'x6', pb: isMobile ? 'x6' : 'x8', px: isMobile ? 'x11' : 'x6' }}
          backgroundColor={cssVar(theme.card)}
        >
          <Typography tag="p" fontSize={1.4} lineHeight={2} fontWeight="bold" spacing={{ mb: 'x6' }}>
            ممنون که به بهتر شدن زوبین کمک میکنی
          </Typography>
          <Col size={12}>
            <CustomLabel>
              <Typography tag="span" fontSize={1.2} lineHeight={2} fontWeight="bold">
                مشکل را توضیح بده
              </Typography>
              <small className="mandatory">*</small>
            </CustomLabel>
          </Col>
          <Col size={12}>
            <ProblemDescriptionTextArea
              spacing={{ my: 'x' }}
              {...register('description')}
              placeholder="لطفا مشکل رو کمی توضیح بده تا در اسرع وقت بررسی کنیم"
            />
            <ErrorFlexBox
              alignItems="center"
              gap={Spacing.x}
              isVisible={!!errors.description}
              spacing={{ mt: 'x' }}
            >
              <Error fill={cssVar(theme.primary1)} />
              <Typography textColor={cssVar(theme.primary1)}>{errors.description?.message}</Typography>
            </ErrorFlexBox>
          </Col>
        </Box>
        <Box spacing={{ py: 'x4', px: isMobile ? 'x11' : 'x6' }} backgroundColor={cssVar(theme.background1)}>
          <Flex gap={Spacing.x4} justifyContent={isMobile ? 'space-between' : 'flex-start'}>
            <FlexDiv flexVal={isMobile ? '2' : '0 1 auto'}>
              <Button type="submit" size={isMobile ? 'full width' : 'fit'} disabled={isLoading}>
                ثبت گزارش
              </Button>
            </FlexDiv>
            <FlexDiv flexVal={isMobile ? '1' : '0 1 auto'}>
              <Button size={isMobile ? 'full width' : 'fit'} variant="outline" onClick={onClose}>
                <Typography fontSize={1.2} textColor={cssVar(theme.grey1)} fontWeight="bold">
                  انصراف
                </Typography>
              </Button>
            </FlexDiv>
          </Flex>
        </Box>
      </form>
    </FormProvider>
  );
};

export default ProblemReportContent;

const ProblemDescriptionTextArea = styled(TextArea)`
  height: 13.6rem;
  line-height: 1.85;
  font-size: 1.3rem;
  background-color: inherit;
  color: var(${theme.text});
`;

const FlexDiv = styled.div<{ flexVal: string }>`
  flex: ${({ flexVal }) => flexVal};
`;

const CustomLabel = styled.div`
  .mandatory {
    color: var(${theme.primary1});
    font-size: 1.4rem;
  }
`;

const ErrorFlexBox = styled(Flex)<{ isVisible: boolean }>`
  opacity: ${({ isVisible }) => (isVisible ? '1' : '0')};
  transition: all 0.1s;
`;
